// src/pages/DmarcDashboard.jsx

import React, { useState, useRef, useEffect } from "react";
// NUEVO: usamos el cliente con interceptor
import { api } from "../services/api";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import ReportePdf from "./components/ReportePdf";
import UserHeader from "../components/UserHeader";
import { isCliente, isEspecialista, getCurrentUser } from "../utils/auth";
import { expiresSoon } from "../services/auth";

ChartJS.register(ArcElement, Tooltip, Legend);

// Base que será /api/dmarc en producción
const API_BASE = process.env.REACT_APP_API_BASE || "/api/dmarc";
// Para el PDF (endpoint /reports/dmarc.pdf) necesitamos la raíz sin /dmarc
const API_ROOT = (process.env.REACT_APP_API_BASE || "/api/dmarc").replace(
  /\/dmarc\/?$/,
  "",
);

// ===================== Utils =====================

// Formato compacto: 129500 -> "129.5k"
const formatCompact = (n) => {
  const num = Number(n || 0);
  if (num >= 1_000_000)
    return (num / 1_000_000).toFixed(num % 1_000_000 ? 1 : 0) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(num % 1_000 ? 1 : 0) + "k";
  return String(num);
};

// Barra de cumplimiento (0-100)
const ComplianceBar = ({ pct }) => {
  const p = Math.max(0, Math.min(100, Number(pct || 0)));
  return (
    <div className="w-full">
      <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-4 rounded-full"
          style={{
            width: `${p}%`,
            background:
              "linear-gradient(90deg, #00C4CC 0%, #00C4CC 85%, #414142 100%)",
          }}
        />{" "}
      </div>{" "}
      <div
        className="text-right text-xs mt-1"
        style={{
          color: "#414142",
        }}
      >
        {" "}
        {p.toFixed(1)} %
      </div>{" "}
    </div>
  );
};

// Muestra la bandera SVG desde FlagCDN + el código ISO
const CountryFlagImg = ({ iso, title }) => {
  const cc = (iso || "").trim().toLowerCase();
  if (!cc || cc.length !== 2) {
    return <span className="text-xs text-gray-500"> - </span>;
  }
  const src = `https://flagcdn.com/${cc}.svg`;
  return (
    <span
      className="inline-flex items-center gap-1"
      title={title || cc.toUpperCase()}
    >
      <img
        src={src}
        alt={cc.toUpperCase()}
        width={20}
        height={15}
        loading="lazy"
        className="inline-block rounded-sm border border-gray-200"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />{" "}
      <span className="text-xs text-gray-600"> {cc.toUpperCase()} </span>{" "}
    </span>
  );
};

const CloudPill = ({ vendor }) => (
  <span
    className="px-2 py-0.5 rounded-full text-xs border"
    style={{
      backgroundColor: "#E0FBFC",
      borderColor: "#B3ECEF",
      color: "#007980",
    }}
  >
    {" "}
    {vendor || "-"}{" "}
  </span>
);

function toBool(v) {
  if (v === true || v === false) return v;
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return ["true", "t", "1", "yes", "y", "ok", "si", "sí", "pass"].includes(s);
}

const BoolPill = ({ value }) => {
  const on = toBool(value);
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs ${
        on ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
      }`}
    >
      {" "}
      {on ? "Sí" : "No"}{" "}
    </span>
  );
};

// Normaliza filas devueltas por /ips_que_pasaron_dmarc
const normalizeIpsOK = (rows = []) =>
  rows.map((r) => ({
    recordid: r.recordid ?? r.RecordID ?? r.id ?? null,
    sourceip: r.sourceip ?? r.SourceIP ?? r.ip ?? null,
    dominio: r.dominio ?? r.domain ?? r.Domain ?? null,
    reportorgname: r.reportorgname ?? r.ReportOrgName ?? r.origen ?? null,
    count: Number(r.count ?? r.Count ?? r.total_correos ?? 0),
    dkimaligned:
      r.dkimaligned ?? r.DKIMAligned ?? r.dkim_alineado ?? r["Alineación DKIM"],
    spfaligned:
      r.spfaligned ?? r.SPFAligned ?? r.spf_alineado ?? r["Alineación SPF"],
    disposition: r.disposition ?? r.resultado_dmarc ?? null,
    authtype: r.authtype ?? r.AuthType ?? r["Tipo Evaluación"] ?? null,
    result: r.result ?? r.Result ?? r["Resultado Auth"] ?? null,
    selector: r.selector ?? r.Selector ?? null,
    // NUEVOS
    cloud_guess: r.cloud_guess ?? r.cloud ?? r.vendor ?? null,
    country_iso: r.country_iso ?? r.country ?? r.countryCode ?? null,
  }));

const getNumber = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

const normalizeTotalMensajes = (t) => {
  if (typeof t === "number") return t;
  if (t && typeof t === "object") {
    return getNumber(
      t.total ?? t.Total ?? t.total_mensajes ?? t.TotalMensajes ?? t.count,
    );
  }
  return 0;
};

// --------------------------------------------------
// Defaults de filtros según el rol del usuario
// --------------------------------------------------
const formatDateYYYYMMDD = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultFiltersFromUser = () => {
  const user = getCurrentUser(); // viene del token en localStorage
  const hoy = new Date();
  const haceUnaSemana = new Date();
  haceUnaSemana.setDate(hoy.getDate() - 7);

  const defaultDesde = formatDateYYYYMMDD(haceUnaSemana);
  const defaultHasta = formatDateYYYYMMDD(hoy);

  let defaultDominio = "";

  if (user) {
    const rol = String(user.rol || "").toLowerCase();

    let possibleLists =
      user.dominios || user.domains || user.dominios_asignados || [];

    if (!Array.isArray(possibleLists) && typeof possibleLists === "string") {
      try {
        possibleLists = JSON.parse(possibleLists);
      } catch {
        possibleLists = [];
      }
    }

    if (rol === "cliente") {
      if (Array.isArray(possibleLists) && possibleLists.length > 0) {
        defaultDominio = String(possibleLists[0]).toLowerCase();
      } else if (user.dominio) {
        defaultDominio = String(user.dominio).toLowerCase();
      }
    } else if (rol === "especialista") {
      defaultDominio = "bombesoluciones.com";
    } else {
      if (Array.isArray(possibleLists) && possibleLists.length > 0) {
        defaultDominio = String(possibleLists[0]).toLowerCase();
      }
    }
  }

  return {
    dominio: defaultDominio,
    desde: defaultDesde,
    hasta: defaultHasta,
  };
};

// ===================== Componente =====================
export default function DmarcDashboard() {
  // 🔹 Nuevo: filtros *visualmente* arrancan en blanco para todos
  const [dominio, setDominio] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  // Guardamos los defaults en un ref por si el usuario NO pone fechas
  const defaultsRef = useRef(getDefaultFiltersFromUser());

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [swapped, setSwapped] = useState(false);

  const [totales, setTotales] = useState(null);
  const [dkim, setDkim] = useState(null);
  const [spf, setSpf] = useState(null);
  const [estadisticas, setEstadisticas] = useState([]);
  const [cumplimientoNube, setCumplimientoNube] = useState([]);
  const [ipsInvalidas, setIpsInvalidas] = useState([]);
  const [ipsValidas, setIpsValidas] = useState([]);

  const [sortingCumple, setSortCumple] = useState({
    field: "recordid",
    ascending: false,
  });
  const [sortingNoCumple, setSortNoCumple] = useState({
    field: "header_from",
    ascending: false,
  });
  const [sortResult, setSortResult] = useState(0);

  const vistaCliente = isCliente();
  const vistaEspecialista = isEspecialista();

  const [porExpirar, setPorExpirar] = useState(false);
  const [ejecutivo, setEjecutivo] = useState("");

  const PAGE_SIZE = 10;

  useEffect(() => {
    vencePronto();
    const copyIpsInvalidas = [...ipsInvalidas];
    const copyIpsValidas = [...ipsValidas];

    const sortedIpsInvalidas = copyIpsInvalidas.sort((a, b) => {
      return a[sortingNoCumple.field]
        ?.toString()
        .localeCompare(b[sortingNoCumple.field]);
    });

    var sortedIpsValidas = [];

    if (sortingCumple.field == "result") {
      const order1 = ["pass", "fail", "temperror"];
      const order2 = ["fail", "temperror", "pass"];
      const order3 = ["temperror", "pass", "fail"];

      const orderMap = new Map(
        (sortResult === 0 ? order1 : sortResult === 1 ? order2 : order3).map(
          (key, index) => [key, index],
        ),
      );

      sortedIpsValidas = copyIpsValidas.sort((a, b) => {
        const ia = orderMap.has(a.result) ? orderMap.get(a.result) : Infinity;
        const ib = orderMap.has(b.result) ? orderMap.get(b.result) : Infinity;
        return ia - ib;
      });
    } else {
      sortedIpsValidas = copyIpsValidas.sort((a, b) => {
        return a[sortingCumple.field]
          ?.toString()
          .localeCompare(b[sortingCumple.field]);
      });
    }

    setIpsInvalidas(
      sortingNoCumple.ascending
        ? sortedIpsInvalidas
        : sortedIpsInvalidas.reverse(),
    );

    setIpsValidas(
      sortingCumple.ascending ? sortedIpsValidas : sortedIpsValidas.reverse(),
    );
  }, [sortingCumple, sortingNoCumple]);

  // Sorting
  const applySorting = (field, ascending, tablaCumplen) => {
    if (tablaCumplen) {
      setSortCumple({
        field: field,
        ascending: ascending,
      });
    } else {
      setSortNoCumple({
        field: field,
        ascending: ascending,
      });
    }
  };

  // paginación para IPs que PASARON DMARC
  const [pageOk, setPageOk] = useState(1);
  const totalPagesOk = Math.max(
    1,
    Math.ceil((ipsValidas?.length || 0) / PAGE_SIZE),
  );
  const ipsValidasPage = React.useMemo(() => {
    const start = (pageOk - 1) * PAGE_SIZE;
    return (ipsValidas || []).slice(start, start + PAGE_SIZE);
  }, [ipsValidas, pageOk]);

  // paginación para IPs que NO PASAN DMARC
  const [pageBad, setPageBad] = useState(1);
  const totalPagesBad = Math.max(
    1,
    Math.ceil((ipsInvalidas?.length || 0) / PAGE_SIZE),
  );
  const ipsInvalidasPage = React.useMemo(() => {
    const start = (pageBad - 1) * PAGE_SIZE;
    return (ipsInvalidas || []).slice(start, start + PAGE_SIZE);
  }, [ipsInvalidas, pageBad]);

  const normalizeParams = () => {
    const dom = (dominio || "").trim().toLowerCase();

    // Fechas tal como las ve el usuario
    let d = (desde || "").trim();
    let h = (hasta || "").trim();

    // Si el usuario NO puso fechas, usamos el rango por defecto (últimos 7 días)
    if (!d || !h) {
      const def = defaultsRef.current || getDefaultFiltersFromUser();
      d = def.desde;
      h = def.hasta;
      // reflejamos en la UI para que vea qué rango se usó
      setDesde(d);
      setHasta(h);
    }

    const d1 = new Date(d);
    const d2 = new Date(h);

    if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
      return {
        dominio: dom,
        desde: d,
        hasta: h,
      };
    }

    if (d1 > d2) {
      setDesde(h);
      setHasta(d);
      setSwapped(true);
      return {
        dominio: dom,
        desde: h,
        hasta: d,
      };
    }

    setSwapped(false);
    return {
      dominio: dom,
      desde: d,
      hasta: h,
    };
  };

  const vencePronto = async () => {
    let data = await expiresSoon(localStorage.getItem("auth_correo"));
    setPorExpirar(data.expires_soon);
    if (data.expires_soon) {
      setEjecutivo(data.executive);
    }
  };

  const fetchAll = async () => {
    const params = normalizeParams();

    if (!params.dominio) {
      setError("Debes ingresar un dominio para consultar el dashboard.");
      return;
    }

    setLoading(true);
    setError("");

    const req = (path, extraConfig = {}) =>
      api.get(path, {
        params,
        ...extraConfig,
      });

    try {
      const results = await Promise.allSettled([
        req("/totales"),
        req("/dmarc_dkim"),
        req("/dmarc_spf"),
        req("/estadisticas_dominios"),
        req("/ips_invalidas", {
          timeout: 60000,
        }),
        req("/ips_que_pasaron_dmarc", {
          timeout: 120000,
        }),
        req("/cumplimiento_por_nube"),
      ]);

      const errs = [];

      // 0) Totales
      if (results[0].status === "fulfilled") {
        setTotales(results[0].value.data);
      } else {
        errs.push("totales");
        setTotales({
          total: 0,
        });
      }

      // 1) DKIM
      if (results[1].status === "fulfilled") {
        setDkim(results[1].value.data);
      } else {
        errs.push("dmarc_dkim");
        setDkim({
          total_dkim_pass_alineado: 0,
        });
      }

      // 2) SPF
      if (results[2].status === "fulfilled") {
        setSpf(results[2].value.data);
      } else {
        errs.push("dmarc_spf");
        setSpf({
          total_spf_pass_alineado: 0,
        });
      }

      // 3) Estadísticas
      if (results[3].status === "fulfilled") {
        const es = results[3].value.data;
        setEstadisticas(Array.isArray(es) ? es : es || []);
      } else {
        errs.push("estadisticas_dominios");
        setEstadisticas([]);
      }

      // 4) IPs inválidas
      if (results[4].status === "fulfilled") {
        const inv = results[4].value.data;
        setIpsInvalidas(Array.isArray(inv) ? inv : inv || []);
      } else {
        errs.push("ips_invalidas");
        setIpsInvalidas([]);
      }

      // 5) IPs que pasaron
      if (results[5].status === "fulfilled") {
        const ok = results[5].value.data;
        const ipsOKRows = Array.isArray(ok) ? ok : ok?.rows || ok || [];
        setIpsValidas(normalizeIpsOK(ipsOKRows));
      } else {
        errs.push("ips_que_pasaron_dmarc");
        setIpsValidas([]);
      }

      // 6) Cumplimiento por nube
      if (results[6].status === "fulfilled") {
        const arr = results[6].value.data || [];
        const sorted = [...arr].sort(
          (a, b) =>
            Number(b.total_mensajes || 0) - Number(a.total_mensajes || 0),
        );
        setCumplimientoNube(sorted);
      } else {
        errs.push("cumplimiento_por_nube");
        setCumplimientoNube([]);
      }

      if (errs.length) {
        setError(`No pude completar: ${errs.join(", ")}. El resto cargó bien.`);
        console.warn("Fallas en:", errs);
          console.warn(results);
      }

      setPageOk(1);
      setPageBad(1);
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.error || e.message || "Error al consultar API",
      );
    } finally {
      setLoading(false);
    }
  };

  const porcentajePassDmarc = React.useMemo(() => {
    const row0 =
      Array.isArray(estadisticas) && estadisticas.length
        ? estadisticas[0]
        : null;
    const val = Number(row0?.porcentaje_pass_dmarc);
    return Number.isFinite(val) ? val : 0;
  }, [estadisticas]);

  const makePieFromPass = (passPct, dummyTotal, labelOk) => {
    const ok = Math.max(0, Math.min(100, Number(passPct || 0)));
    const otros = Math.max(0, 100 - ok);
    return {
      labels: [labelOk, "Otros"],
      datasets: [
        {
          data: [ok, otros],
          backgroundColor: ["#00C4CC", "#E5E7EB"],
          borderColor: ["#FFFFFF", "#FFFFFF"],
          borderWidth: 2,
        },
      ],
    };
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
    },
  };

  const totalMensajes = normalizeTotalMensajes(totales);

  const Pager = ({ page, totalPages, onPrev, onNext }) => (
    <div className="flex items-center justify-between mt-3">
      <button
        className="px-3 py-1 border rounded disabled:opacity-50"
        onClick={onPrev}
        disabled={page <= 1}
      >
        ←Anterior
      </button>
      <div className="text-sm text-gray-600">
        Página {page}
        de {totalPages}
      </div>
      <button
        className="px-3 py-1 border rounded disabled:opacity-50"
        onClick={onNext}
        disabled={page >= totalPages}
      >
        Siguiente→
      </button>
    </div>
  );

  return (
    <div
      className="min-h-screen p-4 md:p-8 relative"
      style={{
        backgroundColor: "#F5F5F5",
      }}
    >
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header con info de usuario */}
        <UserHeader />
        {/* Mensaje según rol */}
        <section>

          {vistaCliente && (
            <div className="rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 text-xs text-blue-800">
              Estás viendo la <strong> vista de cliente </strong>. Los reportes
              y dominios están restringidos a tu organización.
            </div>
          )}
          {vistaEspecialista && (
            <div className="rounded-lg bg-purple-50 border border-purple-100 px-4 py-3 text-xs text-purple-800">
              Estás en la <strong> vista de especialista Bombé </strong>. Puedes
              analizar múltiples dominios de clientes según el filtro
              seleccionado.
            </div>
          )}
          {porExpirar && (
            <div className="rounded-lg bg-orange-100 border border-orange-600 px-4 py-3 text-xs text-orange-600">
             Su cuenta de está <strong>por expirar</strong>. Por favor, contactese con su
              ejecutivo/a: <strong> {ejecutivo}.</strong>
            </div>
          )}
        </section>
        {/* Barra de filtros fija arriba */}
        <div className="sticky top-0 z-40 bg-[#F5F5F5] pb-3">
          <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h1
              className="text-2xl md:text-3xl font-semibold"
              style={{
                color: "#414142",
              }}
            >
              Panel DMARC
            </h1>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 w-full md:w-auto">
              <input
                className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00C4CC] focus:border-[#00C4CC]"
                placeholder="Dominio"
                value={dominio}
                onChange={(e) => setDominio(e.target.value)}
              />
              <input
                type="date"
                className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00C4CC] focus:border-[#00C4CC]"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
              />
              <input
                type="date"
                className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00C4CC] focus:border-[#00C4CC]"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
              />
              <button
                onClick={fetchAll}
                disabled={loading}
                className="text-white font-semibold rounded-lg px-5 py-2 shadow disabled:opacity-50 disabled:cursor-not-allowed transition"
                style={{
                  backgroundColor: "#00C4CC",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "#00AEB5")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "#00C4CC")
                }
              >

                {loading ? "Cargando..." : "Consultar"}
              </button>
            </div>
          </header>
          {/* Mini resumen debajo (opcional, pero queda fino 😎) */}
          <p className="mt-1 text-xs text-gray-500">
            Dominio: <span className="font-medium"> {dominio || "—"} </span> ·
            Rango: {desde || "-"}→ {hasta || "-"}
          </p>
        </div>
        {/* Exportar PDF */}
        <section>
          <ReportePdf
            dominio={dominio}
            desde={desde}
            hasta={hasta}
            apiRoot={API_ROOT}
          />
        </section>
        {swapped && (
          <div className="bg-amber-100 border border-amber-300 text-amber-800 px-4 py-2 rounded-lg">
            El rango de fechas estaba invertido y se corrigió
            automáticamente.
          </div>
        )}
        {error && (
          <div className="bg-red-100 border border-red-300 text-red-800 px-4 py-3 rounded-lg">

            {error}
          </div>
        )}
        {/* Fila superior */} {/* Fila superior */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* Tarjeta TOTAL DE MENSAJES */}
          <div
            className="rounded-2xl shadow p-6 flex flex-col items-center justify-center text-center"
            style={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #E5E7EB",
              minHeight: "260px",
            }}
          >
            <h2
              className="text-xl font-semibold mb-3"
              style={{
                color: "#414142",
              }}
            >
              Total de Mensajes
            </h2>
            <div
              className="font-bold"
              style={{
                fontSize: "4rem", // más grande
                lineHeight: 1,
                color: "#000000",
                marginBottom: "8px",
              }}
            >

              {totalMensajes || (loading ? "…" : "-")}
            </div>
            <p
              className="text-sm"
              style={{
                color: "#6B7280",
              }}
            >
              Rango: {desde || "-"}→ {hasta || "-"}
            </p>
          </div>
          {/* Tarjeta DMARC: Mensajes que pasan (la de siempre) */}
          <div
            className="rounded-2xl shadow p-5"
            style={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #E5E7EB",
            }}
          >
            <h3
              className="text-base font-medium mb-3"
              style={{
                color: "#414142",
              }}
            >
              DMARC: Mensajes que pasan
            </h3>
            <div
              className="mx-auto"
              style={{
                width: 300,
                height: 300,
              }}
            >
              <Pie
                data={makePieFromPass(porcentajePassDmarc, 100, "DMARC Pass %")}
                options={pieOptions}
              />
            </div>
            <div
              className="text-center mt-2 text-sm"
              style={{
                color: "#6B7280",
              }}
            >
              Porcentaje PASS(backend): {porcentajePassDmarc.toFixed(2)} %
            </div>
          </div>
        </section>
        {/* KPIs */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            className="rounded-2xl shadow p-5"
            style={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #E5E7EB",
            }}
          >
            <h3
              className="text-base font-medium mb-1"
              style={{
                color: "#414142",
              }}
            >
              DKIM alineado(pass + aligned)
            </h3>
            <div className="flex items-end gap-3">
              <div
                className="text-4xl font-bold"
                style={{
                  color: "#000000",
                }}
              >

                {Number(dkim?.total_dkim_pass_alineado || 0)}
              </div>
              <div
                style={{
                  color: "#6B7280",
                }}
              >
                de {totalMensajes}(
                {totalMensajes > 0
                  ? (
                      (Number(dkim?.total_dkim_pass_alineado || 0) /
                        totalMensajes) *
                      100
                    ).toFixed(1)
                  : "0.0"}
                % )
              </div>
            </div>
          </div>
          <div
            className="rounded-2xl shadow p-5"
            style={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #E5E7EB",
            }}
          >
            <h3
              className="text-base font-medium mb-1"
              style={{
                color: "#414142",
              }}
            >
              SPF alineado(pass + aligned)
            </h3>
            <div className="flex items-end gap-3">
              <div
                className="text-4xl font-bold"
                style={{
                  color: "#000000",
                }}
              >

                {Number(spf?.total_spf_pass_alineado || 0)}
              </div>
              <div
                style={{
                  color: "#6B7280",
                }}
              >
                de {totalMensajes}(
                {totalMensajes > 0
                  ? (
                      (Number(spf?.total_spf_pass_alineado || 0) /
                        totalMensajes) *
                      100
                    ).toFixed(1)
                  : "0.0"}
                % )
              </div>
            </div>
          </div>
        </section>
        {/* Cumplimiento por remitente (cloud/nube) */}
        <section
          className="rounded-2xl shadow p-5"
          style={{
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
          }}
        >
          <h3
            className="text-lg font-medium mb-3"
            style={{
              color: "#414142",
            }}
          >
            Cumplimiento por remitente
          </h3>
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-100 text-left">
                  <th className="px-3 py-2"> Remitente </th>
                  <th className="px-3 py-2"> Dominio </th>
                  <th className="px-3 py-2"> Volumen </th>
                  <th className="px-3 py-2"> Cumplimiento </th>
                </tr>
              </thead>
              <tbody>

                {loading ? (
                  <tr>
                    <td className="px-3 py-3" colSpan={4}>
                      Cargando...
                    </td>
                  </tr>
                ) : cumplimientoNube?.length ? (
                  cumplimientoNube.map((row, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="px-3 py-2">
                        <CloudPill vendor={row.cloud_guess || "-"} />
                      </td>
                      <td className="px-3 py-2">
                        <a
                          href={`/?dominio=${encodeURIComponent(row.domain)}`}
                          className="underline text-cyan-700 hover:text-cyan-900"
                          title="Filtrar por dominio"
                          onClick={(e) => {
                            e.preventDefault();
                            if (row.domain) {
                              setDominio(row.domain);
                              // Ahora NO dispara carga automática, el usuario
                              // debe hacer clic en "Consultar"
                            }
                          }}
                        >

                          {row.domain}
                        </a>
                      </td>
                      <td className="px-3 py-2 tabular-nums">

                        {formatCompact(row.total_mensajes)}
                      </td>
                      <td className="px-3 py-2">
                        <ComplianceBar pct={row.pass_pct} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="px-3 py-3 text-gray-500" colSpan={4}>
                      Sin datos
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        {/* IPs que PASARON */}
        <section
          className="rounded-2xl shadow p-5"
          style={{
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
          }}
        >
          <h3
            className="text-lg font-medium mb-3"
            style={{
              color: "#414142",
            }}
          >
            IPs que cumplen DMARC
          </h3>
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-100 text-left">
                  <th
                    className="px-3 py-2"
                    onClick={() =>
                      applySorting("recordid", !sortingCumple.ascending, true)
                    }
                  >

                    RecordID
                  </th>
                  <th
                    className="px-3 py-2"
                    onClick={() =>
                      applySorting("sourceip", !sortingCumple.ascending, true)
                    }
                  >

                    IP Fuente
                  </th>
                  <th
                    className="px-3 py-2"
                    onClick={() =>
                      applySorting("dominio", !sortingCumple.ascending, true)
                    }
                  >

                    Dominio(política)
                  </th>
                  <th
                    className="px-3 py-2"
                    onClick={() =>
                      applySorting(
                        "reportorgname",
                        !sortingCumple.ascending,
                        true,
                      )
                    }
                  >

                    Fuente
                  </th>
                  <th
                    className="px-3 py-2"
                    onClick={() =>
                      applySorting(
                        "country_iso",
                        !sortingCumple.ascending,
                        true,
                      )
                    }
                  >

                    País
                  </th>
                  <th
                    className="px-3 py-2"
                    onClick={() =>
                      applySorting("count", !sortingCumple.ascending, true)
                    }
                  >

                    Total correos
                  </th>
                  <th
                    className="px-3 py-2"
                    onClick={() =>
                      applySorting(
                        "dkimaligned",
                        !sortingCumple.ascending,
                        true,
                      )
                    }
                  >

                    DKIM Alineado
                  </th>
                  <th
                    className="px-3 py-2"
                    onClick={() =>
                      applySorting("spfaligned", !sortingCumple.ascending, true)
                    }
                  >

                    SPF Alineado
                  </th>
                  <th
                    className="px-3 py-2"
                    onClick={() =>
                      applySorting(
                        "disposition",
                        !sortingCumple.ascending,
                        true,
                      )
                    }
                  >

                    Disposición DMARC
                  </th>
                  <th
                    className="px-3 py-2"
                    onClick={() =>
                      applySorting("authtype", !sortingCumple.ascending, true)
                    }
                  >

                    AuthType
                  </th>
                  <th
                    className="px-3 py-2"
                    onClick={() => {
                      setSortResult(sortResult + 1 > 2 ? 0 : sortResult + 1);
                      applySorting("result", !sortingCumple.ascending, true);
                    }}
                  >

                    Resultado Auth
                  </th>
                  <th
                    className="px-3 py-2"
                    onClick={() =>
                      applySorting("selector", !sortingCumple.ascending, true)
                    }
                  >

                    Selector
                  </th>
                </tr>
              </thead>
              <tbody>

                {loading ? (
                  <tr>
                    <td className="px-3 py-3" colSpan={12}>
                      Cargando...
                    </td>
                  </tr>
                ) : ipsValidasPage?.length ? (
                  ipsValidasPage.map((row, idx) => (
                    <tr key={idx} className="border-b last:border-0">
                      <td className="px-3 py-2 font-mono">
                        <a
                          href={`/record/${encodeURIComponent(row.recordid)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline text-cyan-700 hover:text-cyan-900"
                          title="Ver detalle de autenticación (por RecordID)"
                        >

                          {row.recordid}
                        </a>
                      </td>
                      <td className="px-3 py-2"> {row.sourceip} </td>
                      <td className="px-3 py-2"> {row.dominio} </td>
                      <td className="px-3 py-2">
                        <CloudPill vendor={row.cloud_guess} />
                      </td>
                      <td className="px-3 py-2">
                        <CountryFlagImg iso={row.country_iso} />
                      </td>
                      <td className="px-3 py-2"> {row.count} </td>
                      <td className="px-3 py-2">
                        <BoolPill value={row.dkimaligned} />
                      </td>
                      <td className="px-3 py-2">
                        <BoolPill value={row.spfaligned} />
                      </td>
                      <td className="px-3 py-2"> {row.disposition} </td>
                      <td className="px-3 py-2"> {row.authtype} </td>
                      <td className="px-3 py-2"> {row.result} </td>
                      <td className="px-3 py-2">

                        {row.selector ?? "-"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="px-3 py-3 text-gray-500" colSpan={12}>
                      Sin datos
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {ipsValidas?.length > PAGE_SIZE && (
            <Pager
              page={pageOk}
              totalPages={totalPagesOk}
              onPrev={() => setPageOk((p) => Math.max(1, p - 1))}
              onNext={() => setPageOk((p) => Math.min(totalPagesOk, p + 1))}
            />
          )}
        </section>
        {/* IPs que NO pasan */}
        <section
          className="rounded-2xl shadow p-5"
          style={{
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
          }}
        >
          <h3
            className="text-lg font-medium mb-3"
            style={{
              color: "#414142",
            }}
          >
            IPs que no cumplen DMARC
          </h3>
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-100 text-left">
                  <th
                    className="px-3 py-2"
                    onClick={() =>
                      applySorting(
                        "cloud_guess",
                        !sortingNoCumple.ascending,
                        false,
                      )
                    }
                  >

                    Fuente
                  </th>
                  <th
                    className="px-3 py-2"
                    onClick={() =>
                      applySorting(
                        "country_iso",
                        !sortingNoCumple.ascending,
                        false,
                      )
                    }
                  >

                    País
                  </th>
                  <th
                    className="px-3 py-2"
                    onClick={() =>
                      applySorting("ip", !sortingNoCumple.ascending, false)
                    }
                  >

                    IP
                  </th>
                  <th
                    className="px-3 py-2"
                    onClick={() =>
                      applySorting(
                        "header_from",
                        !sortingNoCumple.ascending,
                        false,
                      )
                    }
                  >

                    Dominio
                  </th>
                  <th
                    className="px-3 py-2"
                    onClick={() =>
                      applySorting(
                        "sending_domain",
                        !sortingNoCumple.ascending,
                        false,
                      )
                    }
                  >

                    Dominio que envía
                  </th>
                  <th
                    className="px-3 py-2"
                    onClick={() =>
                      applySorting("fuente", !sortingNoCumple.ascending, false)
                    }
                  >

                    Fuente
                  </th>
                  <th
                    className="px-3 py-2"
                    onClick={() =>
                      applySorting(
                        "mensajes_fail",
                        !sortingNoCumple.ascending,
                        false,
                      )
                    }
                  >

                    Mensajes
                  </th>
                </tr>
              </thead>
              <tbody>

                {loading ? (
                  <tr>
                    <td className="px-3 py-3" colSpan={7}>
                      Cargando...
                    </td>
                  </tr>
                ) : ipsInvalidasPage?.length ? (
                  ipsInvalidasPage.map((row, idx) => (
                    <tr
                      key={`${row.ip}-${idx}`}
                      className="border-b last:border-0"
                    >
                      <td className="px-3 py-2">
                        <CloudPill vendor={row.cloud_guess} />
                      </td>
                      <td className="px-3 py-2">
                        <CountryFlagImg iso={row.country_iso} />
                      </td>
                      <td className="px-3 py-2 font-mono">
                        <a
                          href={
                            `/detalle-ip-invalida?` +
                            new URLSearchParams({
                              ip: row.ip.trim(),
                              dominio,
                              desde,
                              hasta,
                            }).toString()
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline text-cyan-700 hover:text-cyan-900"
                          title="Ver detalle de autenticación de esta IP"
                        >

                          {row.ip}
                        </a>
                      </td>
                      <td className="px-3 py-2"> {row.header_from} </td>
                      <td className="px-3 py-2"> {row.sending_domain} </td>
                      <td className="px-3 py-2"> {row.fuente} </td>
                      <td className="px-3 py-2"> {row.mensajes_fail} </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="px-3 py-3 text-gray-500" colSpan={7}>
                      Sin datos
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {ipsInvalidas?.length > PAGE_SIZE && (
            <Pager
              page={pageBad}
              totalPages={totalPagesBad}
              onPrev={() => setPageBad((p) => Math.max(1, p - 1))}
              onNext={() => setPageBad((p) => Math.min(totalPagesBad, p + 1))}
            />
          )}
        </section>
      </div>
    </div>
  );
}
